import type { Card, CardIdentifier } from "@memento/types/card";

import config from "src/config";
import constants from "src/constants";
import { toAbsolutePath } from "./urlLib";
import { daysSinceLastCardActivity } from "@memento/library/card";
import { Compilation } from "@memento/types/compilation";

export function cardTitleToUrl(title: string) {
  return title
    .toLowerCase()
    .replace(/[^\w ]+/g, "")
    .replace(/ +/g, "-");
}

export function getRecordUrl({ cardId, cardUrl }: CardIdentifier) {
  return toAbsolutePath(
    "/" + cardUrl + "/" + cardId + "/" + constants.slugs.recordLink
  );
}

function formatToTwoDigits(seconds: number) {
  return seconds < 10 ? "0" + seconds : seconds;
}

export function secondsToTime(rawSeconds: number) {
  const roundSeconds = Math.round(rawSeconds);
  if (roundSeconds < 60) {
    return roundSeconds + "s";
  } else {
    const minutes = Math.floor(roundSeconds / 60);
    const seconds = formatToTwoDigits(roundSeconds % 60);
    return minutes + ":" + seconds + "s";
  }
}

export function secondsToMMSS(seconds: number) {
  const roundSeconds = Math.round(seconds);
  const secondsPortion = roundSeconds % 60;
  const minutesPortion = Math.floor(roundSeconds / 60);

  return `${minutesPortion.toLocaleString("en-US", {
    minimumIntegerDigits: 2,
    useGrouping: false,
  })}:${secondsPortion.toLocaleString("en-US", {
    minimumIntegerDigits: 2,
    useGrouping: false,
  })}`;
}

export function isValidText(text: string) {
  // Match all characters supported by the font used in memento-render
  if (text === undefined || text === null) {
    return false;
  }
  return !!text.match(/^[\x20-\xFE]*$/);
}

/*
 * Removes invalid characters from string
 */
export function filterText(text: string) {
  return text
    .split("")
    .filter((x) => isValidText(x))
    .join("");
}

export function isCardInArchiveAlertWindow(
  card?: Card,
  compilations?: Compilation[] | null
) {
  if (!card) {
    return false;
  }

  const daysInactive = daysSinceLastCardActivity(card, compilations);

  return (
    daysInactive >= config.DAYS_UNTIL_ARCHIVING_NOTICE &&
    daysInactive < config.DAYS_UNTIL_ARCHIVED
  );
}

export function getUserDisplayName(user: any) {
  return user
    ? user["custom:display_name"] !== undefined
      ? user["custom:display_name"]
      : user["name"]
    : "";
}

export function getCardId(recordUrl?: string) {
  if (recordUrl) {
    const slashSplit = recordUrl.split("/");

    if (slashSplit.length > 2) {
      return slashSplit[slashSplit.length - 2];
    }
  }

  return null;
}

export function getCardUrl(
  cardUrl: string | undefined | null,
  cardId: string | undefined | null
) {
  if (!cardUrl || !cardId) {
    return "/";
  } else {
    return `/${constants.slugs.cardLink}/${cardUrl}/${cardId}`;
  }
}

export function getRenderV3Url({ cardId, cardUrl }: CardIdentifier) {
  return `/group-videos/${cardUrl}/${cardId}/create`;
}

export function getIsDueDatePassed(dueDate: number): boolean {
  if (!dueDate) {
    return true;
  }

  return dueDate <= Date.now();
}

export function getDripEmailFromCards(cards: Card[] | null): string {
  const cardWithDripEmail = cards?.find((card: Card) =>
    Boolean(card.dripInfo?.email)
  );

  return cardWithDripEmail?.dripInfo?.email || "";
}
